import React, { Fragment, useEffect } from 'react'
import { AdvancedButton } from './AdvancedButton';
import { Link, useNavigate } from 'react-router-dom';
import { ButtonGroup, Dropdown, DropdownButton } from 'react-bootstrap';

export interface PageHeaderAction {
    text: string,
    processing?: boolean,
    icon?: string,
    link?: string,
    newTabLink?: boolean,
    onClick?: () => void
    disabled?: boolean
}

interface Props {
    title: string | React.ReactNode,
    description?: string | React.ReactNode,
    showBackButton?: boolean,
    ignoreEscape?: boolean,
    titleSuffix?: React.ReactNode,
    actionsPrefix?: React.ReactNode,
    actions?: PageHeaderAction[]
    className?: string
    contained?: boolean

}

export default function PageHeader(props: Props) {
    const navigate = useNavigate();

    useEffect(() => {
        if (!props.showBackButton || props.ignoreEscape) {
            return;
        }

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                navigate(-1);
            }
        };

        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [props.showBackButton, props.ignoreEscape, navigate])

    const renderPrimaryAction = (action: PageHeaderAction) => {
        const primaryButton = <AdvancedButton icon={action.icon}
            processing={action.processing}
            variant="primary" onClick={action.onClick}
            disabled={action.disabled}
        >{action.text}

        </AdvancedButton>

        if (action.link) {
            return <Link to={action.link}>{primaryButton}</Link>
        }

        return primaryButton;
    }

    const renderSecondaryAction = (action: PageHeaderAction) => {
        if (action.link) {
            return <Dropdown.Item as={Link} to={action.link} target={action.newTabLink ? '_blank' : undefined} >
                {action.icon && <i className={`bi ${action.icon} me-2`} />}
                {action.text}
            </Dropdown.Item>
        }

        return <Dropdown.Item onClick={action.onClick}>
            {action.icon && <i className={`bi ${action.icon} me-2`} />}
            {action.text}
        </Dropdown.Item>
    }

    const renderActions = () => {
        const [primaryAction, ...secondaryActions] = props.actions || [];
        if (!primaryAction) {
            return <></>;
        }

        const primaryButton = renderPrimaryAction(primaryAction);

        if (secondaryActions.length) {
            return <ButtonGroup>
                {primaryButton}
                <DropdownButton drop='down-centered' as={ButtonGroup} variant="primary" title="">
                    {secondaryActions.map((action, index) => <Fragment key={index}>
                        {renderSecondaryAction(action)}
                    </Fragment>)}
                </DropdownButton>
            </ButtonGroup>
        }

        return primaryButton;
    }

    return (
        <div className={`d-flex justify-content-between align-items-start mb-4 ${props.className} ${props.contained ? 'container pt-2' : ''}`}>
            <div className='d-flex flex-column gap-1 align-items-start'>
                <div className='center gap-2'>
                    {props.showBackButton && <AdvancedButton onClick={() => navigate(-1)} icon='bi-chevron-left' size='lg' className='p-0 m-0' variant='clear' />}
                    <h4 className='m-0'>{props.title}</h4>
                    {props.titleSuffix}
                </div>
                {props.description && <p className='m-0 text-secondary'>{props.description}</p>}
            </div>

            <div className='center gap-2'>
                {props.actionsPrefix}
                {renderActions()}
            </div>
        </div>
    )
}
